import { isNullOrEmpty } from 'common-utils';
import { VersionUtils } from 'common-utils/version-utils';
import { ComponentType } from 'dto/raptor-component.dto';
import { UploadFileType } from 'dto/upload-file.dto';
import {
    ComponentLifecycleRequest,
    ComponentManifestRequest,
    ComponentVersionStatus,
    CreateComponentVersionRequest,
    ManifestPlatformDto,
    UpdateComponentVersionRequest,
} from 'dto/raptor-component-version.dto';
import React from 'react';
import { SuccessTag, WarningTag } from 'ui-lib';
import { ISupportedPlatformSummaryItem } from './supported-platforms-summary';
import { IComponentVersionFormValues, IComponentVersionManifestValues } from './component-version-wizard.types';

export const COMPONENT_VERSION_FORMAT_MESSAGE = 'Version must use format MAJOR.MINOR.PATCH, for example 1.2.3';

export class RaptorComponentVersionHelpers {
    public static getArtifactUploadFileType(type: ComponentType | string): UploadFileType | null {
        switch (type) {
            case ComponentType.CATAPULT:
                return UploadFileType.CATAPULT;
            case ComponentType.DOCKER_FILE:
                return UploadFileType.DOCKER_FILE;
            case ComponentType.DOCKER_COMPOSE:
                return UploadFileType.DOCKER_COMPOSE;
            default:
                return null;
        }
    }

    public static validateConfigurationJson(componentType: ComponentType | string, configurationJson?: string): string | null {
        if (componentType !== ComponentType.CATAPULT || !configurationJson?.trim()) {
            return null;
        }

        try {
            JSON.parse(configurationJson);
            return null;
        } catch (error) {
            return error instanceof Error ? error.message : 'Invalid JSON';
        }
    }

    public static isConfigurationValid(
        componentType: string,
        values: IComponentVersionFormValues,
    ): boolean {
        if (componentType === ComponentType.CATAPULT) {
            return !this.validateConfigurationJson(componentType, values.configurationJson);
        }

        const platformUuids = values.platformUuids || [];
        return !isNullOrEmpty(platformUuids)
            && platformUuids.every((uuid) => this.isManifestComplete(componentType, values.manifests?.[uuid]));
    }

    public static isManifestComplete(componentType: string, manifest: IComponentVersionManifestValues = {}): boolean {
        switch (componentType) {
            case ComponentType.CATAPULT:
                return true;
            case ComponentType.DOCKER_FILE:
                return !!manifest.uploadedFiles?.length && manifest.dockerImages?.length === 1;
            case ComponentType.DOCKER_IMAGE:
                return manifest.dockerImages?.length === 1;
            case ComponentType.DOCKER_COMPOSE:
                return !!manifest.uploadedFiles?.length && !!manifest.dockerImages?.length;
            default:
                return false;
        }
    }
}

export class ComponentVersionDisplayHelpers {
    public static formatDisplayValue(value: unknown): string {
        if (value === undefined || value === null || value === '') {
            return '-';
        }
        return String(value);
    }

    public static getComponentVersionDisplayName(componentName: string, version: string): string {
        return `${componentName} (${version})`;
    }

    public static renderRecords<T>(records: T[] | undefined, key: keyof T): React.ReactNode {
        if (!records?.length) {
            return '-';
        }
        return (
            <div>
                {records.map((record, index) => (
                    <div key={index}>{this.formatDisplayValue(record[key])}</div>
                ))}
            </div>
        );
    }

    public static getSupportedPlatformItems(platforms: ManifestPlatformDto[]): ISupportedPlatformSummaryItem[] {
        return platforms.map((platform) => ({
            key: platform.uuid,
            label: platform.displayName || '-',
        }));
    }

    public static renderStatus(status: ComponentVersionStatus): React.ReactNode {
        if (status === ComponentVersionStatus.PUBLISHED) {
            return <SuccessTag value={status} />;
        }
        return <WarningTag value={status ?? 'Unknown'} />;
    }
}

export const validateComponentVersionFormat = (version?: string): string | null => {
    const trimmedVersion = version?.trim();
    if (!trimmedVersion) {
        return null;
    }

    if (VersionUtils.isValidVersion(trimmedVersion)) {
        return null;
    }

    return COMPONENT_VERSION_FORMAT_MESSAGE;
};

export const buildEmptyComponentVersionFormValues = (): IComponentVersionFormValues => {
    return {
        version: '',
        description: undefined,
        platformUuids: [],
        configurationJson: '',
        manifests: {},
    };
};

export const compactObject = (value: Record<string, unknown>): Record<string, unknown> => {
    return Object.entries(value || {}).reduce((result, [key, entryValue]) => {
        if (entryValue === undefined || entryValue === null || entryValue === '') {
            return result;
        }
        result[key] = entryValue;
        return result;
    }, {} as Record<string, unknown>);
};

export const buildManifests = (
    componentType: ComponentType | string,
    values: IComponentVersionFormValues,
): ComponentManifestRequest[] => {
    const selectedPlatformUuids = values.platformUuids || [];

    return selectedPlatformUuids.map((platformUuid) => {
        const manifestValue = values.manifests?.[platformUuid] || {};

        switch (componentType) {
            case ComponentType.CATAPULT: {
                const lifecycle = compactObject({
                    install: manifestValue.lifecycle?.install,
                    run: manifestValue.lifecycle?.run,
                    startup: manifestValue.lifecycle?.startup,
                }) as ComponentLifecycleRequest;
                return {
                    platformUuid,
                    lifecycle,
                    uploadedFileUuids: manifestValue.uploadedFiles?.map((file) => file.uuid),
                };
            }
            case ComponentType.DOCKER_IMAGE: {
                return {
                    platformUuid,
                    dockerImageUuids: manifestValue.dockerImages?.map((dockerImage) => dockerImage.uuid),
                };
            }
            case ComponentType.DOCKER_FILE:
            case ComponentType.DOCKER_COMPOSE: {
                return {
                    platformUuid,
                    uploadedFileUuids: manifestValue.uploadedFiles?.map((file) => file.uuid),
                    dockerImageUuids: manifestValue.dockerImages?.map((dockerImage) => dockerImage.uuid),
                };
            }
            default:
                return { platformUuid };
        }
    });
};

export const buildCreateComponentVersionPayload = (
    componentType: ComponentType | string,
    values: IComponentVersionFormValues,
): CreateComponentVersionRequest => {
    return {
        version: values.version?.trim() || '',
        description: values.description?.trim() || undefined,
        configuration: values.configurationJson?.trim() ? JSON.parse(values.configurationJson) : {},
        manifests: buildManifests(componentType, values),
    };
};

export const buildUpdateComponentVersionPayload = (
    componentType: ComponentType | string,
    values: IComponentVersionFormValues,
): UpdateComponentVersionRequest => {
    const payload = buildCreateComponentVersionPayload(componentType, values);
    return {
        description: values.description?.trim() || '',
        configuration: payload.configuration,
        manifests: payload.manifests,
    };
};
